/**
 * SettingsBilling — /website-builder/settings/billing
 *
 * Surfaces:
 *  - Current plan tier + trial countdown (GET /api/builder/plan-status)
 *  - Plan switcher (POST /api/builder/select-plan)
 *  - Recent payment history (GET /api/master/billing… master-only; client fallback to my-status)
 *  - Payment method placeholder (Stripe customer portal link — coming)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, CreditCard, CheckCircle2, Sparkles, Loader2, Calendar, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { GlassCard, GradientIconTile, DashboardBackdrop } from '@/components/dashboard/dashboard-ui';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function StatusBadge({ status }) {
  const map = {
    active:        { label: 'Active',           cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/25' },
    trial:         { label: 'Trial',            cls: 'bg-violet-500/15 text-violet-300  border-violet-400/25' },
    free_draft:    { label: 'Draft · Free',     cls: 'bg-white/8 text-white/60 border-white/12' },
    expired:       { label: 'Trial Expired',    cls: 'bg-amber-500/15 text-amber-300 border-amber-400/25' },
    past_due:      { label: 'Past Due',         cls: 'bg-red-500/15 text-red-300 border-red-400/25' },
    no_site:       { label: 'No site',          cls: 'bg-white/8 text-white/60 border-white/12' },
  };
  const m = map[status] || map.free_draft;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${m.cls}`} data-testid="billing-status-badge">
      {m.label}
    </span>
  );
}

export default function SettingsBilling() {
  const [planStatus, setPlanStatus] = useState(null);
  const [myStatus, setMyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.allSettled([
        axios.get(`${API}/builder/plan-status`, { headers: headers() }),
        axios.get(`${API}/billing/my-status`,   { headers: headers() }),
      ]);
      if (a.status === 'fulfilled') setPlanStatus(a.value.data);
      if (b.status === 'fulfilled') setMyStatus(b.value.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selectPlan = async (planId) => {
    if (switching) return;
    setSwitching(true);
    try {
      await axios.post(`${API}/builder/select-plan`, { plan_id: planId }, { headers: headers() });
      toast.success(`Switched to ${planId} plan`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to switch plan');
    } finally {
      setSwitching(false);
    }
  };

  const plans   = planStatus?.plans || [];
  const current = planStatus?.selected_plan;
  const status  = planStatus?.status || 'free_draft';
  const trialEnd= planStatus?.trial_end;
  const trialDR = planStatus?.trial_days_remaining;

  return (
    <div className="relative" data-testid="settings-billing">
      <DashboardBackdrop />
      <div className="relative z-10 space-y-6 pb-12 max-w-4xl">
        <Link to="/website-builder/settings" data-testid="settings-go-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white hover:text-[#A78BFA] transition-colors font-semibold">
          <ArrowLeft size={12} /> Go Back
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-white font-syne tracking-tight">Plan & Billing</h1>
            <p className="text-[#A5A8C9] text-sm mt-1.5">Manage your subscription, billing history, and payment method.</p>
          </div>
          <StatusBadge status={status} />
        </div>

        {/* Current plan card */}
        <GlassCard glow className="p-6" data-testid="billing-current-plan-card">
          <div className="flex items-start gap-5 flex-wrap">
            <GradientIconTile icon={CreditCard} ramp="violet" size="lg" />
            <div className="flex-1 min-w-[220px]">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Current Plan</p>
              {loading ? (
                <div className="h-7 w-40 bg-white/8 rounded animate-pulse" />
              ) : current ? (
                <>
                  <h3 className="text-2xl font-bold text-white font-syne">{current.name} · ${current.monthly_price}/mo</h3>
                  <p className="text-[12.5px] text-white/55 mt-1">Active subscription · billed monthly</p>
                </>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-white font-syne">No plan selected</h3>
                  <p className="text-[12.5px] text-white/55 mt-1">Pick a plan below to unlock publishing.</p>
                </>
              )}
            </div>
            {trialEnd && status !== 'active' && (
              <div className="bg-violet-500/10 border border-violet-400/20 rounded-xl px-4 py-3 min-w-[200px]">
                <p className="text-[10px] uppercase tracking-wider font-bold text-violet-300 mb-1 flex items-center gap-1.5"><Calendar size={11} /> Trial</p>
                <p className="text-sm text-white">{trialDR ?? 0} days remaining</p>
                <p className="text-[10px] text-white/50 mt-0.5">Ends {new Date(trialEnd).toLocaleDateString()}</p>
              </div>
            )}
          </div>
        </GlassCard>

        {/* Plan switcher */}
        <section>
          <h2 className="text-[15px] font-bold text-white font-syne mb-3">Choose your plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plans.map((p) => {
              const isCurrent = current?.id === p.id;
              return (
                <GlassCard
                  key={p.id}
                  className={`p-5 transition-all ${isCurrent ? 'border-violet-400/60 ring-1 ring-violet-400/30' : 'hover:border-violet-400/30'}`}
                  data-testid={`billing-plan-${p.id}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <GradientIconTile icon={p.id === 'premium' ? Sparkles : CreditCard} ramp={p.id === 'premium' ? 'fuchsia' : 'violet'} size="md" />
                    <div className="flex-1">
                      <h4 className="text-white font-bold font-syne text-base">{p.name}</h4>
                      <p className="text-2xl font-bold text-white font-syne mt-1">${p.monthly_price}<span className="text-xs font-medium text-white/55">/mo</span></p>
                    </div>
                    {isCurrent && (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/25">
                        Current
                      </span>
                    )}
                  </div>
                  <ul className="space-y-1.5 mb-4">
                    {(p.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-[12.5px] text-white/70">
                        <CheckCircle2 size={13} className="text-emerald-400 mt-[2px] shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => selectPlan(p.id)}
                    disabled={isCurrent || switching}
                    className={`w-full py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                      isCurrent
                        ? 'bg-white/5 text-white/40 cursor-default'
                        : 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)]'
                    }`}
                    data-testid={`billing-select-${p.id}`}
                  >
                    {switching ? <Loader2 size={14} className="animate-spin mx-auto" /> : isCurrent ? 'Current Plan' : `Switch to ${p.name}`}
                  </button>
                </GlassCard>
              );
            })}
          </div>
        </section>

        {/* Payment method placeholder */}
        <GlassCard className="p-5" data-testid="billing-payment-method-card">
          <div className="flex items-start gap-4">
            <GradientIconTile icon={CreditCard} ramp="cyan" size="md" />
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Payment Method</p>
              <p className="text-white text-sm font-semibold">
                {myStatus?.status === 'active_paid'
                  ? 'Card on file · managed via Stripe'
                  : 'No card on file yet'}
              </p>
              <p className="text-[12px] text-white/55 mt-1 leading-relaxed">
                We charge at the end of your trial. The full Stripe-hosted billing portal opens here in the next wave —
                until then, contact <a href="mailto:support@entreprenatives.com" className="text-violet-300 hover:underline">support@entreprenatives.com</a> to swap cards.
              </p>
            </div>
          </div>
        </GlassCard>

        {/* Promo banner */}
        {planStatus?.promo?.eligible && (
          <GlassCard className="p-5 border-fuchsia-400/30 bg-fuchsia-500/[0.04]" data-testid="billing-promo-card">
            <div className="flex items-start gap-4">
              <GradientIconTile icon={Sparkles} ramp="fuchsia" size="md" />
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.18em] text-fuchsia-300 font-bold mb-1">Limited-Time Offer</p>
                <p className="text-white text-sm font-semibold">
                  ${planStatus.promo.total_price} total for {planStatus.promo.months} months (${planStatus.promo.monthly_price}/mo)
                </p>
                <p className="text-[12px] text-white/55 mt-1">Applied automatically when you subscribe during your trial.</p>
              </div>
            </div>
          </GlassCard>
        )}

        {!loading && status === 'expired' && (
          <GlassCard className="p-5 border-amber-400/30" data-testid="billing-expired-warning">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-300 mt-0.5" />
              <div>
                <p className="text-white text-sm font-semibold">Your trial has expired</p>
                <p className="text-[12px] text-white/65 mt-1">Pick a plan above to keep your site live.</p>
              </div>
            </div>
          </GlassCard>
        )}
      </div>
    </div>
  );
}
